/*----------------------------------
- HOME PLATFORM CAPABILITIES (SSOT)
----------------------------------*/

// Copy for the home section that follows the workflow bento. The bento tells the
// linear buy story, tile by numbered tile; this catalog holds what sits beside that
// path rather than on it, the evidence behind a verdict, and the guarantees around a
// credit.
//
// Admission rule for this file: an entry either names something a member can use
// today, or carries `comingSoon` and is written in the future tense.
//
// The Domain sales card left the announced group with ADR-0067: `/sales` publishes the
// newest usable sales and the nightly figures, and members read every sale in the product.
//
// The Domaining Agenda card left that group on 2026-09-29: the scanner now reads TLD
// launch phases and root zone changes from IANA and ICANN, the events carry a category
// and a place, and `/agenda` publishes all of it, so the card links there in the
// present tense.

import type { IconName } from '@uniquedomains/components/ui/icon';
import { ANALYSIS_MAX_RUN_SIZE } from '@uniquedomains/core/domains/analysis';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

/*----------------------------------
- TYPES
----------------------------------*/

export type HomePlatformCapability = {
    id: string;
    iconName: IconName;
    /**
     * Drives `--ud-product-accent` / `--ud-product-accent-soft` on the card, which is
     * what tints the shared glass icon orb. Values come from the same six-colour
     * palette the "Most tools help you look." cards use, so the two grids read as one
     * page rather than two unrelated colour systems.
     */
    accent: string;
    accentSoft: string;
    title: string;
    body: string;
    /**
     * Set only where a published marketing page exists. Analyse and Team ship in the
     * product but have no `/product/:slug` page yet, so their cards stay unlinked
     * rather than pointing at a workspace behind the auth wall, and the section
     * stays off the conversion path until those pages are written.
     */
    href?: string;
    /**
     * Renders the "Coming soon" pill and pairs the card on the section's last row.
     * A `comingSoon` body is written in the future tense, matching the bento's
     * "Find buyers" tile, so the copy never reads as an available feature.
     */
    comingSoon?: boolean;
};

/*----------------------------------
- CATALOG (SSOT)
----------------------------------*/

export const HOME_PLATFORM_SECTION_COPY = {
    heading: 'One toolbox. The whole job.',
    subheading:
        'Valuation, comparison, Radars with a brief, industry news, and the market record behind a price. Every tool a domain investor needs after the search, in one product.',
} as const;

// Titles are the product's own feature names, matching the Product mega menu labels,
// so a visitor meets the same word on the home page, in the menu, and in the app.
// Bodies stay near fifteen words: the card has to be readable by someone who has
// never bought a domain, and the feature page behind it carries the detail.
export const HOME_PLATFORM_CAPABILITIES: readonly HomePlatformCapability[] = [
    {
        id: 'appraise',
        accent: '#a15d1a',
        accentSoft: '#fef3c7',
        iconName: 'coins',
        title: 'Appraise',
        body: 'Value one name or a whole list at once, through several appraisal sources instead of one.',
        href: LANDING_WEBSITE_PAGE_ROUTES.productAppraise,
    },
    {
        id: 'analyse',
        accent: '#315fa8',
        accentSoft: '#dbeafe',
        iconName: 'columns',
        title: 'Analyse',
        body: `Put up to ${ANALYSIS_MAX_RUN_SIZE} domains side by side, every signal on the same row.`,
    },
    {
        id: 'radars',
        accent: '#6b21a8',
        accentSoft: '#f3e8ff',
        iconName: 'radio',
        title: 'Radars',
        body: 'A Radar per brief: its daily matches, the names you save, and your finalists in one place.',
        href: LANDING_WEBSITE_PAGE_ROUTES.productRadars,
    },
    {
        id: 'news',
        accent: '#0f766e',
        accentSoft: '#ccfbf1',
        iconName: 'feed',
        title: 'Domaining News',
        body: 'Cited daily briefings on what is moving in the domain industry, plus a weekly newsletter.',
        href: LANDING_WEBSITE_PAGE_ROUTES.domainingNews,
    },
    {
        id: 'sales-history',
        accent: '#0e7490',
        accentSoft: '#cffafe',
        iconName: 'chart-line',
        title: 'Domain sales',
        body: 'Reported sales updated each night: what names sold for, by ending and word length.',
        href: LANDING_WEBSITE_PAGE_ROUTES.sales,
    },
    {
        id: 'domaining-calendar',
        accent: '#c2416c',
        accentSoft: '#ffe4e6',
        iconName: 'calendar',
        title: 'Domaining Agenda',
        body: 'New TLD launch dates, root zone changes and industry events, with a calendar feed.',
        href: LANDING_WEBSITE_PAGE_ROUTES.agenda,
    },
] as const;
